<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Modal from 'dashboard/components/Modal.vue';
import orderIssuesAPI from 'dashboard/api/orderIssues';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  allProblemTags: {
    type: Array,
    default: () => [],
  },
  allActionTags: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'success']);

const { t } = useI18n();
const loading = ref(false);
const saving = ref(false);

// Tag settings: { tag_name: color }
const problemTagColors = ref({});
const actionTagColors = ref({});

const fetchTagSettings = async () => {
  loading.value = true;
  try {
    const response = await orderIssuesAPI.getTagSettings();
    const data = response.data;
    
    // Convert array to object for easier manipulation
    problemTagColors.value = {};
    actionTagColors.value = {};
    
    if (data.problem_tags) {
      data.problem_tags.forEach(tag => {
        if (tag.color) {
          problemTagColors.value[tag.tag_name] = tag.color;
        }
      });
    }
    
    if (data.action_tags) {
      data.action_tags.forEach(tag => {
        if (tag.color) {
          actionTagColors.value[tag.tag_name] = tag.color;
        }
      });
    }
  } catch (error) {
    console.error('Failed to fetch tag settings:', error);
    useAlert(t('ORDER_ISSUES.TAG_SETTINGS.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const handleSave = async () => {
  saving.value = true;
  try {
    // Convert objects back to array format for API
    const settings = [];
    
    // Add ALL problem tags (with or without colors)
    props.allProblemTags.forEach(tag_name => {
      settings.push({
        tag_name,
        tag_type: 'problem',
        color: problemTagColors.value[tag_name] || null,
      });
    });
    
    // Add ALL action tags (with or without colors)
    props.allActionTags.forEach(tag_name => {
      settings.push({
        tag_name,
        tag_type: 'action',
        color: actionTagColors.value[tag_name] || null,
      });
    });
    
    console.log('Saving tag settings:', settings);
    await orderIssuesAPI.bulkUpdateTagSettings(settings);
    useAlert(t('ORDER_ISSUES.TAG_SETTINGS.SAVE_SUCCESS'));
    emit('success');
    emit('close');
  } catch (error) {
    console.error('Failed to save tag settings:', error);
    console.error('Error details:', error.response?.data || error.message);
    useAlert(t('ORDER_ISSUES.TAG_SETTINGS.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

const handleClose = () => {
  emit('close');
};

const updateProblemTagColor = (tagName, color) => {
  if (color) {
    problemTagColors.value[tagName] = color;
  } else {
    delete problemTagColors.value[tagName];
  }
};

const updateActionTagColor = (tagName, color) => {
  if (color) {
    actionTagColors.value[tagName] = color;
  } else {
    delete actionTagColors.value[tagName];
  }
};

// Watch for modal visibility changes
watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      fetchTagSettings();
    }
  }
);

// Initialize on mount if modal is already open
onMounted(() => {
  if (props.show) {
    fetchTagSettings();
  }
});
</script>

<template>
  <Modal :show="show" :on-close="handleClose">
    <div class="flex flex-col max-w-2xl">
      <div class="flex items-center justify-between p-4 border-b border-n-weak">
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{ $t('ORDER_ISSUES.TAG_SETTINGS.TITLE') }}
        </h2>
      </div>

      <div v-if="loading" class="p-8 flex items-center justify-center">
        <div class="text-n-slate-11">{{ $t('ORDER_ISSUES.TAG_SETTINGS.LOADING') }}</div>
      </div>

      <div v-else class="p-4 space-y-6 max-h-[70vh] overflow-y-auto">
        <!-- Problem Tags Section -->
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12 mb-3">
            {{ $t('ORDER_ISSUES.TAG_SETTINGS.PROBLEM_TAGS') }}
          </h3>
          <div v-if="allProblemTags.length === 0" class="text-sm text-n-slate-11 italic">
            {{ $t('ORDER_ISSUES.TAG_SETTINGS.NO_PROBLEM_TAGS') }}
          </div>
          <div v-else class="space-y-2">
            <div
              v-for="tag in allProblemTags"
              :key="tag"
              class="flex items-center gap-3 p-2 border border-n-weak rounded-md hover:bg-n-alpha-1"
            >
              <span class="flex-1 text-sm text-n-slate-12">{{ tag }}</span>
              <input
                :value="problemTagColors[tag] || ''"
                type="color"
                class="w-10 h-8 cursor-pointer border border-n-weak rounded"
                @input="updateProblemTagColor(tag, $event.target.value)"
              />
              <button
                v-if="problemTagColors[tag]"
                class="text-xs text-n-slate-11 hover:text-red-500 transition-colors px-2"
                @click="updateProblemTagColor(tag, null)"
              >
                {{ $t('ORDER_ISSUES.TAG_SETTINGS.CLEAR') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Action Tags Section -->
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12 mb-3">
            {{ $t('ORDER_ISSUES.TAG_SETTINGS.ACTION_TAGS') }}
          </h3>
          <div v-if="allActionTags.length === 0" class="text-sm text-n-slate-11 italic">
            {{ $t('ORDER_ISSUES.TAG_SETTINGS.NO_ACTION_TAGS') }}
          </div>
          <div v-else class="space-y-2">
            <div
              v-for="tag in allActionTags"
              :key="tag"
              class="flex items-center gap-3 p-2 border border-n-weak rounded-md hover:bg-n-alpha-1"
            >
              <span class="flex-1 text-sm text-n-slate-12">{{ tag }}</span>
              <input
                :value="actionTagColors[tag] || ''"
                type="color"
                class="w-10 h-8 cursor-pointer border border-n-weak rounded"
                @input="updateActionTagColor(tag, $event.target.value)"
              />
              <button
                v-if="actionTagColors[tag]"
                class="text-xs text-n-slate-11 hover:text-red-500 transition-colors px-2"
                @click="updateActionTagColor(tag, null)"
              >
                {{ $t('ORDER_ISSUES.TAG_SETTINGS.CLEAR') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Info Message -->
        <div class="text-xs text-n-slate-11 bg-n-alpha-1 p-3 rounded-md">
          <i class="i-lucide-info text-sm mr-1" />
          {{ $t('ORDER_ISSUES.TAG_SETTINGS.INFO') }}
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-2 p-4 border-t border-n-weak">
        <button
          class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="handleClose"
        >
          {{ $t('ORDER_ISSUES.TAG_SETTINGS.CANCEL') }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium text-white bg-n-brand hover:bg-n-brand-dark rounded-md transition-colors disabled:opacity-50"
          :disabled="saving"
          @click="handleSave"
        >
          {{ saving ? $t('ORDER_ISSUES.TAG_SETTINGS.SAVING') : $t('ORDER_ISSUES.TAG_SETTINGS.SAVE') }}
        </button>
      </div>
    </div>
  </Modal>
</template>
